import { Event } from '~/redux/actionTypes';
import callAPI from '~/redux/actions/callAPI';
import { eventSchema, eventAdministrateSchema } from '~/redux/schemas';
import type { EntityId } from '@reduxjs/toolkit';
import type { Thunk, Action } from 'app/types';
import type { DetailedEvent } from '~/redux/models/Event';
import type { Presence, ReadRegistration } from '~/redux/models/Registration';
import type { PublicUser } from '~/redux/models/User';

export type RegistrationSearchResult = Omit<ReadRegistration, 'user'> & {
  user: PublicUser;
};

export const waitinglistPoolId = -1;

export function fetchEvents({ query, next = false }) {
  return callAPI({
    types: Event.FETCH,
    endpoint: '/events/',
    schema: [eventSchema],
    query,
    pagination: {
      fetchNext: next,
    },
    meta: {
      errorMessage: 'Henting av arrangementer feilet',
    },
    propagateError: true,
  });
}

export function fetchEvent(eventId: EntityId) {
  return callAPI<DetailedEvent>({
    types: Event.FETCH,
    endpoint: `/events/${eventId}/`,
    schema: eventSchema,
    meta: {
      errorMessage: 'Henting av arrangement feilet',
    },
    propagateError: true,
  });
}

export function fetchPrevious() {
  return callAPI({
    types: Event.FETCH_PREVIOUS,
    endpoint: '/events/previous/',
    schema: [eventSchema],
    meta: {
      errorMessage: 'Henting av tidligere arrangementer feilet',
    },
    pagination: { fetchNext: false },
    propagateError: true,
  });
}

export function fetchUpcoming() {
  return callAPI({
    types: Event.FETCH_UPCOMING,
    endpoint: '/events/upcoming/',
    schema: [eventSchema],
    meta: {
      errorMessage: 'Henting av kommende arrangementer feilet',
    },
    pagination: { fetchNext: false },
    propagateError: true,
  });
}

export function fetchAdministrate(
  eventId: EntityId,
  { propagateError = true } = {},
) {
  return callAPI({
    types: Event.FETCH,
    endpoint: `/events/${eventId}/administrate/`,
    schema: eventAdministrateSchema,
    propagateError,
    meta: {
      errorMessage: 'Henting av registreringer feilet',
    },
  });
}

export function fetchAllergies(eventId: EntityId) {
  return callAPI({
    types: Event.FETCH,
    endpoint: `/events/${eventId}/allergies/`,
    schema: eventAdministrateSchema,
    meta: {
      errorMessage: 'Henting av allergier feilet',
    },
  });
}

export function createEvent(event: Record<string, any>) {
  return callAPI<DetailedEvent>({
    types: Event.CREATE,
    endpoint: '/events/',
    method: 'POST',
    body: event,
    schema: eventSchema,
    meta: {
      errorMessage: 'Opprettelse av arrangement feilet',
    },
  });
}

export function editEvent(event: Record<string, any>) {
  return callAPI<DetailedEvent>({
    types: Event.EDIT,
    endpoint: `/events/${event.id}/`,
    method: 'PUT',
    body: { ...event, cover: event.cover || undefined },
    meta: {
      errorMessage: 'Endring av arrangement feilet',
    },
  });
}

export function editPartialEvent(
  event: { id: EntityId } & Partial<DetailedEvent>,
) {
  return callAPI<DetailedEvent>({
    types: Event.EDIT,
    endpoint: `/events/${event.id}/`,
    method: 'PATCH',
    body: { ...event, cover: event.cover || undefined },
    meta: {
      errorMessage: 'Endring av arrangement feilet',
      successMessage: 'Endring av arrangement fullført',
    },
  });
}

export function deleteEvent(eventId: EntityId) {
  return callAPI({
    types: Event.DELETE,
    endpoint: `/events/${eventId}/`,
    method: 'DELETE',
    meta: {
      id: eventId,
      errorMessage: 'Sletting av arrangement feilet',
    },
  });
}

export function register({
  eventId,
  captchaResponse,
  feedback,
  userId,
}: {
  eventId: EntityId;
  captchaResponse: string;
  feedback: string;
  userId: EntityId;
}) {
  return callAPI({
    types: Event.REQUEST_REGISTER,
    endpoint: `/events/${eventId}/registrations/`,
    method: 'POST',
    body: {
      captchaResponse,
      feedback,
    },
    meta: {
      id: eventId,
      userId,
      errorMessage: 'Registering til arrangement feilet',
    },
  });
}

export function unregister({
  eventId,
  registrationId,
}: {
  eventId: EntityId;
  registrationId: EntityId;
}) {
  return callAPI({
    types: Event.REQUEST_UNREGISTER,
    endpoint: `/events/${eventId}/registrations/${registrationId}/`,
    method: 'DELETE',
    body: {},
    meta: {
      errorMessage: 'Avregistrering fra arrangement feilet',
      id: Number(registrationId),
    },
  });
}

export function adminRegister(
  eventId: EntityId,
  userId: EntityId,
  poolId: EntityId | undefined,
  feedback: string,
  adminRegistrationReason: string,
) {
  return callAPI({
    types: Event.ADMIN_REGISTER,
    endpoint: `/events/${eventId}/registrations/admin_register/`,
    method: 'POST',
    body: {
      user: userId,
      pool: poolId === waitinglistPoolId ? undefined : poolId,
      adminRegistrationReason,
      feedback,
    },
    meta: {
      errorMessage: 'Admin registrering feilet',
      successMessage: 'Brukeren ble registrert',
    },
  });
}

export function adminUnregister(
  eventId: EntityId,
  userId: EntityId,
  registrationId: EntityId,
  adminUnregistrationReason: string,
) {
  return callAPI({
    types: Event.REQUEST_UNREGISTER,
    endpoint: `/events/${eventId}/registrations/admin_unregister/`,
    method: 'POST',
    body: {
      user: userId,
      adminUnregistrationReason,
    },
    meta: {
      errorMessage: 'Admin avregistrering feilet',
      successMessage: 'Brukeren ble avregistrert',
      admin: true,
      id: Number(registrationId),
    },
  });
}

export function payment(eventId: EntityId) {
  return callAPI({
    types: Event.PAYMENT_QUEUE,
    endpoint: `/events/${eventId}/payment/`,
    method: 'POST',
    meta: {
      errorMessage: 'Betaling feilet',
    },
  });
}

export function updateFeedback(
  eventId: EntityId,
  registrationId: EntityId,
  feedback: string,
) {
  return callAPI({
    types: Event.UPDATE_REGISTRATION,
    endpoint: `/events/${eventId}/registrations/${registrationId}/`,
    method: 'PATCH',
    body: {
      feedback,
    },
    meta: {
      successMessage: 'Tilbakemelding oppdatert',
      errorMessage: 'Tilbakemelding oppdatering feilet',
    },
  });
}

export function markUsernamePresent(eventId: EntityId, username: string) {
  return callAPI<RegistrationSearchResult>({
    types: Event.UPDATE_REGISTRATION,
    endpoint: `/events/${eventId}/registration_search/`,
    method: 'POST',
    body: {
      username,
    },
  });
}

export function markQrPresent(eventId: EntityId, qr: string) {
  return callAPI<RegistrationSearchResult>({
    types: Event.UPDATE_REGISTRATION,
    endpoint: `/events/${eventId}/registration_search/`,
    method: 'POST',
    body: {
      qr,
    },
  });
}

export function updatePresence(
  eventId: EntityId,
  registrationId: EntityId,
  presence: Presence,
) {
  return callAPI({
    types: Event.UPDATE_REGISTRATION,
    endpoint: `/events/${eventId}/registrations/${registrationId}/`,
    method: 'PATCH',
    body: {
      presence,
    },
    meta: {
      successMessage: 'Tilstedeværelse oppdatert',
      errorMessage: 'Oppdatering av tilstedeværelse feilet',
    },
  });
}

export function updatePayment(
  eventId: EntityId,
  registrationId: EntityId,
  paymentStatus: string,
): Thunk<Promise<Action | null | undefined>> {
  return callAPI({
    types: Event.UPDATE_REGISTRATION,
    endpoint: `/events/${eventId}/registrations/${registrationId}/`,
    method: 'PATCH',
    body: {
      paymentStatus,
    },
    meta: {
      errorMessage: 'Oppdatering av betaling feilet',
    },
  });
}

export function follow(userId: EntityId, eventId: EntityId) {
  return callAPI({
    types: Event.FOLLOW,
    enableOptimistic: true,
    endpoint: `/followers-event/`,
    method: 'POST',
    body: {
      target: eventId,
      follower: userId,
    },
    meta: {
      errorMessage: 'Registrering av interesse feilet',
    },
  });
}

export function unfollow(followId: EntityId, eventId: EntityId) {
  return callAPI({
    types: Event.UNFOLLOW,
    endpoint: `/followers-event/${followId}/`,
    enableOptimistic: true,
    method: 'DELETE',
    meta: {
      id: followId,
      eventId,
      errorMessage: 'Avregistrering fra interesse feilet',
    },
  });
}

export function fetchFollowers(eventId: EntityId, currentUserId: EntityId) {
  return callAPI({
    types: Event.FETCH_FOLLOWERS,
    endpoint: `/followers-event/?target=${eventId}`,
    method: 'GET',
    meta: {
      eventId,
      currentUserId,
      errorMessage: 'Henting av stjernemarkering feilet',
    },
  });
}
